import * as React from 'react';
import { BreadcrumbGroup, Button, Flashbar, FlashbarProps, SpaceBetween, Tabs, Badge, Spinner, Link } from "@amzn/awsui-components-react-v3"
import { Pane } from '../components/shared/HelpPane/HelpPane.constants'
import { Type } from '../components/shared/HelpPane/questionnairePane/QuestionnairePane.constants'
import HistoryTable from "../components/item-history-table"
import ActionTable from '../components/ActionTable'
import { ItemModalComps } from "../components/ItemDetailComps/ItemModalComps"
import { ItemTabs } from "../components/ItemDetailComps/ItemTabs"
import { ECCN_STATUS, ITAR_QUESTIONNAIRE_STATUS, ItemDetailModel, UserInfoContext } from '../models/item-detail-model';
import { Builder } from 'builder-pattern';
import { Header } from '@amzn/awsui-components-react-v3';
import { Question_Status } from '../models/question-model';
import { ActionModal } from "./action-modal";
import { connect, useSelector, useDispatch } from "react-redux";
import { RootState } from '../reducers';
import { ItemAccessWarning } from '../components/shared/ItemAccessWarning';
import { fetchItems } from '../reducers/ItemsSlice';

interface ItemDetailProps {
  helpPane: any;
  match: {
    params: {
      id: string;
    }
  };
  location: {
    state: {
      isNew?: boolean
    }
  };
  isAdmin: boolean | null;
}

const ItemDetail = (props: ItemDetailProps) => {
  const itemDetailState = ItemDetailModel.UI.State.use({ id: props.match.params.id });
  const [activeId, setActiveId] = React.useState("details");
  const [showAssignedActionsModal, setShowAssignedActionsModal] = React.useState(false);
  const dispatch = useDispatch();

  // Get lab settings to check if parent/component feature is enabled
  const labSettings = useSelector((state: RootState) => state.labSettingsObj);
  const isParentComponentFeatureEnabled = labSettings.parentComponentItems;

  // State for component items (fetched individually via getItem)
  const [componentItems, setComponentItems] = React.useState<any[]>([]);
  const [itemsLoading, setItemsLoading] = React.useState(false);

  // Function to fetch components using individual getItem calls (Option 3)
  const refreshComponentsData = React.useCallback(async () => {
    setItemsLoading(true);
    try {
      const { getItem } = await import('../actions/async.action');
      
      // Fetch parent item to get the latest components array
      const parentItemRes = await getItem(props.match.params.id);
      const componentIds = parentItemRes.data?.Item?.components || [];
      
      // Update parent item's components array in itemDetailState
      if (parentItemRes.data?.Item?.components) {
        itemDetailState.itemState.setValue({
          ...itemDetailState.itemState.value,
          components: parentItemRes.data.Item.components
        });
      }
      
      // If no components, set empty array and return
      if (componentIds.length === 0) {
        setComponentItems([]);
        setItemsLoading(false);
        return;
      }
      
      // Fetch all component items in parallel using Promise.all
      const componentPromises = componentIds.map(async (id: string) => {
        try {
          const result = await getItem(id);
          return result.data?.Item || null;
        } catch (err) {
          console.error(`Failed to fetch component ${id}:`, err);
          return null; // Return null for failed fetches to handle gracefully
        }
      });
      
      const componentResults = await Promise.all(componentPromises);
      
      // Filter out any null results (failed fetches) and update state
      const validComponents = componentResults.filter(item => item !== null);
      setComponentItems(validComponents);
      
      setItemsLoading(false);
    } catch (err) {
      console.error('Failed to refresh components data:', err);
      setItemsLoading(false);
    }
  }, [props.match.params.id, itemDetailState.itemState]);

  // Initial fetch of components when component mounts or parent's components array changes
  React.useEffect(() => {
    if (itemDetailState.itemState.value.isParent && itemDetailState.itemState.value.components) {
      refreshComponentsData();
    }
  }, [itemDetailState.itemState.value.isParent, itemDetailState.itemState.value.components?.length]);

  const tabs = [
    {
      "label": "Overview",
      "id": "details",
      "content": <ItemTabs.Detail {...itemDetailState} />
    },
    {
      "label": "Questionnaire",
      "id": "questionnaire",
      "content": <ItemTabs.Questionnaire {...itemDetailState} />
    },
    {
      "label": "History",
      "id": "history",
      "content": <HistoryTable itemId={itemDetailState.itemState.value.Id} helpPane={props.helpPane} />,
    },
    {
      "label": "Supplements",
      "id": "supplements",
      "content": <ItemTabs.Supplement {...itemDetailState} />,
    },
    {
      "label": "Assigned actions",
      "id": "Assigned actions",
      "content": <ActionTable itemId={itemDetailState.itemState.value.Id} />,
    },
  ]

  // Tabs with Components (for parent items with CCATs)
  const tabsWithComponents = [
    ...tabs,
    {
      "label": "Related Items",
      "id": "components",
      "content": <ItemTabs.Components 
        {...itemDetailState} 
        componentItems={componentItems}
        onRefreshComponents={refreshComponentsData}
        onShowFlashMessage={(message: FlashbarProps.MessageDefinition) => {
          itemDetailState.flashbarItems.setValue([
            ...itemDetailState.flashbarItems.value,
            message
          ]);
        }}
      />,
    },
  ].filter((tab, index, self) => 
    index === self.findIndex(t => t.id === tab.id)
  );

  const noCCATsTabs = [
    {
      "label": "Overview",
      "id": "details",
      "content": <ItemTabs.Detail {...itemDetailState} />,
    },
    {
      "label": "Questionnaire",
      "id": "questionnaire",
      "content": <ItemTabs.Questionnaire {...itemDetailState} />
    },
    {
      "label": "History",
      "id": "history",
      "content": <HistoryTable itemId={itemDetailState.itemState.value.Id} helpPane={props.helpPane} />,
    },
    {
      "label": "Assigned actions",
      "id": "Assigned actions",
      "content": <ActionTable itemId={itemDetailState.itemState.value.Id} />,
    },
  ]

  // Tabs without CCATs but with Components (for parent items)
  const noCCATsTabsWithComponents = [
    ...noCCATsTabs,
    {
      "label": "Related Items",
      "id": "components",
      "content": <ItemTabs.Components 
        {...itemDetailState} 
        componentItems={componentItems}
        onRefreshComponents={refreshComponentsData}
        onShowFlashMessage={(message: FlashbarProps.MessageDefinition) => {
          itemDetailState.flashbarItems.setValue([
            ...itemDetailState.flashbarItems.value,
            message
          ]);
        }}
      />,
    },
  ]

  const notAuthorizedUserTabs = [
    {
      "label": "Overview",
      "id": "details",
      "content": <ItemTabs.Detail {...itemDetailState} />
    },
  ]

  const notValidResultTabs = [
    {
      "label": "Overview",
      "id": "details",
      "content": <ItemTabs.Detail {...itemDetailState} />
    },
  ]

  const deleteButton = (
    <Button
      loading={itemDetailState.loading.value}
      onClick={() => itemDetailState.deleteModalState.basicModalState.visible.setValue(true)}
    >
      Delete
    </Button>
  )

  const cloneButton = (
    <Button
      loading={itemDetailState.loading.value}
      onClick={() => {
        itemDetailState.cloneModalState.basicModalState.visible.setValue(true)}
      }
    >
      Clone
    </Button>
  )

  const deprecateButton = (
    <Button
      loading={itemDetailState.loading.value}
      disabled={itemDetailState.itemState.value.Deprecated}
      onClick={() => itemDetailState.deprecatedState.basicModalState.visible.setValue(true)}
    >
      Deprecate
    </Button>
  );

  const assignActionButton = (
    <Button
      loading={itemDetailState.loading.value}
      onClick={
        () => itemDetailState.assignActionModalState.basicModalState.visible.setValue(true)}>
      Assign action
    </Button>
  )

  const baselineButton = (
    !itemDetailState.baselineMeta.value.autoBaselineExemption &&
    itemDetailState.itemState.value.ExemptFromBaseline !== "Yes" &&
    <Button
      iconName='status-positive'
      variant='primary'
      loading={itemDetailState.loading.value || itemDetailState.onBaselineRunner.status === "Running"}
      disabled={
        !itemDetailState.baselineMeta.value.activeBaselinePeriod ||
        itemDetailState.baselineMeta.value.currentPeriodBaselined ||
        itemDetailState.onBaselineRunner.status === "Finished" ||
        itemDetailState.itemState.value.ECCNStatus === ECCN_STATUS.PENDING_QUESTIONNAIRE_COMPLETION ||
        (itemDetailState.itemState.value.ITARQuestionnaireStatus === ITAR_QUESTIONNAIRE_STATUS.PENDING_QUESTIONNAIRE_COMPLETION &&
          (itemDetailState.itemState.value.MilitaryUse === "Yes" || itemDetailState.itemState.value.DualUse === "No")) ||
        !!itemDetailState.questionnaireState.questionnaire.value.find((q) => q.status === Question_Status.ARCHIVED) ||
        itemDetailState.itemState.value.CET?.length < 1 ||
        (
          !itemDetailState.itemState.value.SaaSRestrictions.isCNCSoftware ||
          !itemDetailState.itemState.value.SaaSRestrictions.isDesignAndManufacturingSoftware ||
          !itemDetailState.itemState.value.SaaSRestrictions.isEnterpriseManagementSoftware
        )
      }
      onClick={async () => {
        setShowAssignedActionsModal(true)
      }}
    >
      Baseline
    </Button>
  );

  let buttons = <></>;
  let targetTab = notAuthorizedUserTabs
  const deletePermission = (itemDetailState.isContributor.value && itemDetailState.itemState.value.Deletable) || props.isAdmin
  // Only show parent item features if lab setting is enabled
  const isParentItem = isParentComponentFeatureEnabled && itemDetailState.itemState.value.isParent;
  
  if (itemDetailState.isContributor.value) {
    if (isParentItem) {
      targetTab = itemDetailState.itemState.value && itemDetailState.itemState.value.AssignedCCATs ? tabsWithComponents : noCCATsTabsWithComponents;
    } else {
      targetTab = itemDetailState.itemState.value && itemDetailState.itemState.value.AssignedCCATs ? tabs : noCCATsTabs;
    }
    buttons = (
      <SpaceBetween
        direction="horizontal"
        size="xs"
      >
        {itemDetailState.itemState.value && cloneButton}
        {itemDetailState.itemState.value && deprecateButton}
        {itemDetailState.itemState.value && deletePermission && deleteButton}
        {itemDetailState.itemState.value && assignActionButton}
        {itemDetailState.itemState.value && baselineButton}
      </SpaceBetween>
    )
  }

  if (props.isAdmin) {
    targetTab = isParentItem ? tabsWithComponents : tabs
    buttons = (
      <>
        <SpaceBetween direction="horizontal" size="xs">
          {itemDetailState.itemState.value && cloneButton}
          {itemDetailState.itemState.value && deprecateButton}
          {itemDetailState.itemState.value && deletePermission && deleteButton}
          {itemDetailState.itemState.value && assignActionButton}
          {itemDetailState.itemState.value && baselineButton}
        </SpaceBetween>
      </>
    )
  }

  React.useEffect(() => {
    if (props.location.state && props.location.state.isNew) {
      itemDetailState.flashbarItems.setValue([
        ...itemDetailState.flashbarItems.value,
        Builder<FlashbarProps.MessageDefinition>()
          .type("success")
          .content("New Item created. Click on the Questionnaire tab to complete the questions required to classify your Item.")
          .dismissible(true)
          .build()
      ])
    }
  }, [props.location.state])


  // Show loading spinner while admin status is being determined or item is still loading (initial load only)
  // Note: itemDetailState.loading.value was intentionally removed from this condition to avoid spinner
  // flickering during questionnaire answer saves; spinner is only shown on initial load (no Id yet)
  if (props.isAdmin === null || !itemDetailState.itemState.value.Id) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <Spinner size="big" />
      </div>
    );
  }

  return (<>
    <ItemAccessWarning 
      itemDetailState={itemDetailState}
      isAdmin={props.isAdmin}
    />
    {showAssignedActionsModal && 
      <ActionModal itemId={props.match.params.id}
        onSubmit={() => {
          itemDetailState.onBaselineRunner.submitRun(
            itemDetailState.itemState.value.Id
          )
          setShowAssignedActionsModal(false)
        }}
        onCancel={() => {
          setShowAssignedActionsModal(false)
        }}
      />
    }
    <div style={{ padding: '2rem' }} >
      <UserInfoContext.Provider value={{
        isAdmin: props.isAdmin,
        isContributor: itemDetailState.isContributor.value
      }}>
        <Flashbar items={itemDetailState.flashbarItems.value.map((message, index) => {
          return {
            ...message,
            onDismiss: () => {
              const newValue = [...itemDetailState.flashbarItems.value];
              newValue.splice(index, 1)
              itemDetailState.flashbarItems.setValue([
                ...newValue
              ])
            }
          }
        })
        } />
        <BreadcrumbGroup items={itemDetailState.breadcrumb.value}/>
        <ItemModalComps.PerformanceModal state={itemDetailState.performanceModalState} />
        <ItemModalComps.CloneModal state={itemDetailState.cloneModalState} />
        <ItemModalComps.DeprecateModal state={itemDetailState.deprecatedState} />
        <ItemModalComps.AssignActionModal state={itemDetailState.assignActionModalState} />
        <ItemModalComps.DeleteModal state={itemDetailState.deleteModalState} />
        <>
          <Header
            className="align-items-center"
            actions={
              <SpaceBetween
                direction="horizontal"
                size="xs"
              >
                <div >
                  {buttons}
                </div>
              </SpaceBetween>
            }
          >
            <h1 className='badgeTitle cript-page-header'>
              {itemDetailState.itemState.value.ItemFullName}
              {itemDetailState.itemState.value.Deprecated && 
                <span
                  className="awsui-util-pl-xs"
                  style={{ verticalAlign: "text-bottom" }}
                >
                  <Badge>Deprecated</Badge>
                </span>
              }
              <Link
                variant="info"
                className='pad-left-8px'
                href="javascript:void(0);"
                onFollow={() => props.helpPane.dispatch({ pane: Pane.DetailsPane, type: Type.Info, payload: '' })}
              >
                Info
              </Link>
            </h1>
          </Header>
          <Tabs tabs={targetTab} activeTabId={activeId} onChange={(event) => setActiveId(event.detail.activeTabId)}></Tabs>
        </>
      </UserInfoContext.Provider>
    </div>
  </>)
}

const mapStateToProps = (state: RootState) => ({
  isAdmin: state.authenticationObj.isAdmin
});

export default connect(mapStateToProps)(ItemDetail);
